import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { useCanCheck } from '@/ability/helpers';
import { useAuthStore } from '@stores/auth.store';
import StatusBadge from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import {
    createOpenMarketLead,
    getOpenMarketLeadById,
    getOpenMarketLeads,
    getLeadBids,
    placeBid as placeBidApi,
    updateBid as updateBidApi,
    startChat as startChatApi,
    patchLeadState as patchLeadStateApi,
} from '@services/open-market.service';
import LeadStateSwitch from '@/components/ui/LeadStateSwitch';

export const OPEN_MARKET_LEADS_QUERY_KEY = ['open-market', 'leads'];
export const OPEN_MARKET_LEAD_DETAIL_QUERY_KEY = (id) => ['open-market', 'leads', 'detail', id];
export const OPEN_MARKET_LEAD_BIDS_QUERY_KEY = (leadId) => ['open-market', 'leads', leadId, 'bids'];

const STATUS_TAB = {
    ALL: 'all',
    OPEN: 'open',
    CLOSED: 'closed',
};

export const CATEGORY_OPTIONS = [
    { value: 'web_app', label: 'Web App' },
    { value: 'mobile_app', label: 'Mobile App' },
    { value: 'ui_ux', label: 'UI / UX' },
    { value: 'other', label: 'Other' },
];

export const formatCurrencyInput = (value) => {
    if (value == null || value === '') return '';
    const str = String(value).replace(/,/g, '');
    return `$ ${str.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

export const parseCurrencyInput = (displayValue) => {
    if (displayValue == null || displayValue === '') return null;
    const num = parseFloat(String(displayValue).replace(/[$,]/g, ''), 10);
    return Number.isNaN(num) ? null : num;
};

const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');
const formatCategoryLabel = (category) => capFirst((category || '').replace(/_/g, ' ')) || '—';
const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `$${num.toLocaleString()}`;
};

export const useOpenMarketLeadDetail = (leadId) =>
    useQuery({
        queryKey: OPEN_MARKET_LEAD_DETAIL_QUERY_KEY(leadId),
        queryFn: () => getOpenMarketLeadById(leadId),
        enabled: !!leadId,
    });

export const useLeadBids = (leadId) =>
    useQuery({
        queryKey: OPEN_MARKET_LEAD_BIDS_QUERY_KEY(leadId),
        queryFn: () => getLeadBids(leadId),
        enabled: !!leadId,
    });

/** For feature sidebar: list of leads (projects). Same API, shared cache with main list. */
export const useOpenMarketLeadsSidebar = (options = {}) => {
    const { enabled = true } = options;
    const { data, isLoading } = useQuery({
        queryKey: [...OPEN_MARKET_LEADS_QUERY_KEY, { page: 1, limit: 50 }],
        queryFn: () => getOpenMarketLeads({ page: 1, limit: 50 }),
        enabled: !!enabled,
    });
    return { items: data?.items ?? [], isLoading };
};

export const useOpenMarketLeads = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const can = useCanCheck();
    const user = useAuthStore((s) => s.user);
    const role = useAuthStore((s) => s.role);
    const isClientProfile = (role || user?.role || user?.profileType || '') === 'client';
    const canCreateLead = can('open_market.leads.create');
    /** Client: user.isClient or role/roles contains "Client". Show State column only for client, Action column only for non-client. */
    const isClient =
        user?.isClient === true ||
        (typeof role === 'string' && role.toLowerCase() === 'client') ||
        (typeof user?.role === 'string' && user.role.toLowerCase() === 'client') ||
        (Array.isArray(user?.roles) && user.roles.some((r) => String(r).toLowerCase() === 'client'));
    const showStateColumn = isClient;
    const showActionColumn = !isClient;
    const canPlaceBid = can('open_market.bids.create');

    const [form] = Form.useForm();
    const [bidForm] = Form.useForm();
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [bidModalOpen, setBidModalOpen] = useState(false);
    const [bidLeadId, setBidLeadId] = useState(null);

    const createLeadMutation = useMutation({
        mutationFn: (body) => createOpenMarketLead(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEADS_QUERY_KEY });
        },
    });

    const placeBidMutation = useMutation({
        mutationFn: ({ leadId, body }) => placeBidApi(leadId, body),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_DETAIL_QUERY_KEY(leadId) });
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_BIDS_QUERY_KEY(leadId) });
        },
    });

    const patchLeadStateMutation = useMutation({
        mutationFn: ({ leadId, state }) => patchLeadStateApi(leadId, { state }),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_DETAIL_QUERY_KEY(leadId) });
        },
    });

    const approveBidMutation = useMutation({
        mutationFn: ({ bidId }) => updateBidApi(bidId, { status: 'accepted' }),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_DETAIL_QUERY_KEY(leadId) });
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_BIDS_QUERY_KEY(leadId) });
        },
    });

    const declineBidMutation = useMutation({
        mutationFn: ({ bidId }) => updateBidApi(bidId, { status: 'declined' }),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_DETAIL_QUERY_KEY(leadId) });
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_BIDS_QUERY_KEY(leadId) });
        },
    });

    const startChatMutation = useMutation({
        mutationFn: ({ bidId }) => startChatApi(bidId),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEADS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['open-market', 'leads', 'detail'] });
            const raw = data?.data ?? data;
            const chatId = raw?.id ?? raw?.chat?.id ?? raw?.chatId;
            if (chatId) {
                const prefix = location?.pathname?.startsWith('/tenant') ? '/tenant' : '';
                navigate(`${prefix}/chat/${chatId}`);
            }
        },
    });

    const {
        data: response,
        isError: leadsError,
        error: leadsQueryError,
    } = useQuery({
        queryKey: [...OPEN_MARKET_LEADS_QUERY_KEY, { page: 1, limit: 100 }],
        queryFn: () => getOpenMarketLeads({ page: 1, limit: 100 }),
        retry: false,
    });

    const isUpgradeRequired =
        leadsError &&
        leadsQueryError?.response?.status === 403 &&
        String(leadsQueryError?.response?.data?.message || '')
            .toLowerCase()
            .includes('upgrade');

    const leads = response?.items ?? [];
    const total = response?.total ?? 0;

    const openCount = useMemo(
        () => leads.filter((lead) => (lead.status || '').toLowerCase() === 'open').length,
        [leads],
    );
    const closedCount = useMemo(
        () => leads.filter((lead) => (lead.status || '').toLowerCase() === 'closed').length,
        [leads],
    );

    const selectedLeadTitle = useMemo(
        () => leads.find((lead) => lead.id === selectedLeadId)?.title ?? 'Lead details',
        [leads, selectedLeadId],
    );

    const filteredLeads = useMemo(() => {
        let list = leads;
        if (statusTab !== STATUS_TAB.ALL) {
            list = list.filter((lead) => (lead.status || '').toLowerCase() === statusTab);
        }
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (lead) =>
                    (lead.title || '').toLowerCase().includes(q) ||
                    (lead.description || '').toLowerCase().includes(q) ||
                    (lead.category || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [leads, statusTab, searchText]);

    const tableData = useMemo(
        () =>
            filteredLeads.map((lead) => {
                const acceptedBid = lead.bids?.find((b) => (b.status || '').toLowerCase() === 'accepted');
                return {
                    key: lead.id,
                    id: lead.id,
                    title: lead.title,
                    description: lead.description,
                    category: lead.category,
                    budgetMin: lead.budgetMin,
                    budgetMax: lead.budgetMax,
                    status: lead.status,
                    state: lead.state,
                    createdAt: lead.createdAt,
                    myBid: lead.myBid ?? null,
                    clientUserId: lead.clientUserId ?? null,
                    acceptedBidId: acceptedBid?.id ?? null,
                    acceptedBidAmount: acceptedBid?.amount,
                    acceptedBidTenantName: acceptedBid?.tenant?.name,
                };
            }),
        [filteredLeads],
    );

    const getLeadById = useCallback((id) => leads.find((l) => l.id === id) ?? null, [leads]);

    const openBidModal = useCallback((leadId) => {
        setBidLeadId(leadId);
        setBidModalOpen(true);
    }, []);
    const closeBidModal = useCallback(() => {
        setBidModalOpen(false);
        setBidLeadId(null);
    }, []);

    const handlePlaceBid = useCallback(
        (values, callbacks = {}) => {
            if (!bidLeadId) return;
            const amount = Number(values.amount);
            const proposal = (values.proposal ?? '').trim();
            if (Number.isNaN(amount)) {
                message.error('Enter a valid amount');
                return;
            }
            placeBidMutation.mutate(
                {
                    leadId: bidLeadId,
                    body: {
                        amount,
                        proposal,
                    },
                },
                {
                    onSuccess: () => {
                        message.success('Bid placed');
                        closeBidModal();
                        callbacks.onSuccess?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to place bid');
                        callbacks.onError?.(err);
                    },
                },
            );
        },
        [bidLeadId, placeBidMutation, closeBidModal],
    );

    const handleApproveBid = useCallback(
        (bidId, leadId, callbacks = {}) => {
            approveBidMutation.mutate(
                { bidId, leadId },
                {
                    onSuccess: () => {
                        message.success('Bid approved');
                        callbacks.onSuccess?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to approve bid');
                        callbacks.onError?.(err);
                    },
                },
            );
        },
        [approveBidMutation],
    );

    const handleDeclineBid = useCallback(
        (bidId, leadId, callbacks = {}) => {
            declineBidMutation.mutate(
                { bidId, leadId },
                {
                    onSuccess: () => {
                        message.success('Bid declined');
                        callbacks.onSuccess?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to decline bid');
                        callbacks.onError?.(err);
                    },
                },
            );
        },
        [declineBidMutation],
    );

    const handleStartChat = useCallback(
        (bidOrBidId, callbacks = {}) => {
            const bid = typeof bidOrBidId === 'object' && bidOrBidId !== null ? bidOrBidId : null;
            const bidId = bid?.id ?? bidOrBidId;
            const isChatOpened = bid?.isChatOpened === true;
            const existingChatId = bid?.chatId ?? bid?.chat_id ?? null;

            if (isChatOpened || existingChatId) {
                if (existingChatId) {
                    const prefix = location?.pathname?.startsWith('/tenant') ? '/tenant' : '';
                    navigate(`${prefix}/chat/${existingChatId}`);
                } else {
                    const prefix = location?.pathname?.startsWith('/tenant') ? '/tenant' : '';
                    navigate(`${prefix}/chat`);
                }
                callbacks.onSuccess?.({ chatId: existingChatId });
                return;
            }

            startChatMutation.mutate(
                { bidId },
                {
                    onSuccess: (data) => {
                        message.success('Chat started');
                        callbacks.onSuccess?.(data);
                        const raw = data?.data ?? data;
                        const chatId = raw?.id ?? raw?.chat?.id ?? raw?.chatId;
                        if (chatId) {
                            const prefix = location?.pathname?.startsWith('/tenant') ? '/tenant' : '';
                            navigate(`${prefix}/chat/${chatId}`);
                        } else {
                            navigate(location?.pathname?.startsWith('/tenant') ? '/tenant/chat' : '/chat');
                        }
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to start chat');
                        callbacks.onError?.(err);
                    },
                },
            );
        },
        [startChatMutation, navigate, location?.pathname],
    );

    const isClientOfLead = useCallback(
        (record) => !!user?.id && !!record?.clientUserId && String(user.id) === String(record.clientUserId),
        [user?.id],
    );

    const handleStateChange = useCallback(
        (leadId, currentState) => {
            const next = (currentState || '').toLowerCase() === 'running' ? 'paused' : 'running';
            patchLeadStateMutation.mutate(
                { leadId, state: next },
                {
                    onSuccess: () => message.success(`Lead set to ${next === 'running' ? 'Running' : 'Paused'}`),
                    onError: (err) => message.error(err?.response?.data?.message || 'Failed to update lead state'),
                },
            );
        },
        [patchLeadStateMutation],
    );

    const columns = useMemo(
        () => [
            {
                title: 'Project',
                key: 'project',
                render: (_, record) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{record.title || '—'}</div>
                        {record.description && (
                            <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary)' }}>
                                {record.description}
                            </div>
                        )}
                        <div style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                            {formatCategoryLabel(record.category)}
                        </div>
                    </div>
                ),
            },
            {
                title: 'Budget',
                key: 'budget',
                render: (_, record) => `${formatMoney(record.budgetMin)} – ${formatMoney(record.budgetMax)}`,
            },
            {
                title: 'Status',
                key: 'status',
                width: 220,
                render: (_, record) => (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <StatusBadge status={record.status} />
                    </div>
                ),
            },
            ...(showStateColumn
                ? [
                      {
                          title: 'State',
                          dataIndex: 'state',
                          key: 'state',
                          width: 160,
                          render: (val, record) => {
                              const isClient = isClientOfLead(record);
                              if (isClient) {
                                  return (
                                      <span onClick={(e) => e.stopPropagation()}>
                                          <LeadStateSwitch
                                              value={record.state || 'paused'}
                                              onChange={() => handleStateChange(record.id, record.state)}
                                              disabled={patchLeadStateMutation.isPending}
                                              loading={
                                                  patchLeadStateMutation.isPending &&
                                                  patchLeadStateMutation.variables?.leadId === record.id
                                              }
                                          />
                                      </span>
                                  );
                              }
                              return capFirst(val || '—');
                          },
                      },
                  ]
                : []),
            ...(showActionColumn
                ? [
                      {
                          title: 'Action',
                          key: 'action',
                          width: 200,
                          align: 'left',
                          render: (_, record) => {
                              const isClient = isClientOfLead(record);
                              const statusLower = (record.status || '').toLowerCase();
                              const isOpen = statusLower === 'open';
                              const inProgress = statusLower === 'in_progress';
                              const showChat = isClient && inProgress && record.acceptedBidId;
                              const hasBid = !!record.myBid;
                              const bidAmount = record.myBid?.amount;

                              return (
                                  <span
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                                  >
                                      {hasBid ? (
                                          <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary)' }}>
                                              Bid placed: {formatMoney(bidAmount)}
                                          </span>
                                      ) : isOpen && canPlaceBid ? (
                                          <Button
                                              type="button"
                                              $primary={false}
                                              $size="14px"
                                              onClick={() => openBidModal(record.id)}
                                          >
                                              Place Bid
                                          </Button>
                                      ) : null}
                                      {showChat && (
                                          <Button
                                              type="button"
                                              $primary
                                              $blackOnLight
                                              $size="12px"
                                              icon={<MessageOutlined />}
                                              onClick={() => handleStartChat(record.acceptedBidId)}
                                              loading={startChatMutation.isPending}
                                              disabled={startChatMutation.isPending}
                                          >
                                              Chat
                                          </Button>
                                      )}
                                  </span>
                              );
                          },
                      },
                  ]
                : []),
        ],
        [
            showStateColumn,
            showActionColumn,
            openBidModal,
            canPlaceBid,
            isClientOfLead,
            handleStartChat,
            startChatMutation.isPending,
            handleStateChange,
            patchLeadStateMutation.isPending,
            patchLeadStateMutation.variables?.leadId,
        ],
    );

    const handleCreateLead = useCallback(
        (values, callbacks = {}) => {
            const budgetMin = Number(values.budgetMin);
            const budgetMax = Number(values.budgetMax);
            if (Number.isNaN(budgetMin) || Number.isNaN(budgetMax)) {
                message.error('Budget must be a valid number');
                return;
            }
            if (budgetMax < budgetMin) {
                message.error('Max budget must be greater than or equal to min budget');
                return;
            }

            createLeadMutation.mutate(
                {
                    title: values.title?.trim(),
                    description: values.description?.trim(),
                    budgetMin,
                    budgetMax,
                    category: values.category,
                },
                {
                    onSuccess: () => {
                        message.success('Lead created');
                        setCreateModalOpen(false);
                        callbacks.onSuccess?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to create lead');
                        callbacks.onError?.(err);
                    },
                },
            );
        },
        [createLeadMutation],
    );

    const openPanel = (id) => {
        setSelectedLeadId(id);
        setPanelOpen(true);
    };
    const closePanel = () => {
        setPanelOpen(false);
        setSelectedLeadId(null);
    };

    const onSubmitCreate = useCallback(() => {
        form.validateFields().then((values) => {
            handleCreateLead(values, { onSuccess: () => form.resetFields() });
        });
    }, [form, handleCreateLead]);

    const onSubmitBid = useCallback(() => {
        bidForm.validateFields().then((values) => {
            handlePlaceBid(values, { onSuccess: () => bidForm.resetFields() });
        });
    }, [bidForm, handlePlaceBid]);

    return {
        form,
        bidForm,
        onSubmitCreate,
        onSubmitBid,
        total,
        openCount,
        closedCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        panelOpen,
        closePanel,
        openPanel,
        selectedLeadId,
        selectedLeadTitle,
        getLeadById,
        createModalOpen,
        setCreateModalOpen,
        handleCreateLead,
        createLeadPending: createLeadMutation.isPending,
        leadsError,
        isUpgradeRequired,
        upgradeMessage: leadsQueryError?.response?.data?.message || 'Upgrade your plan to access Open Market',
        bidModalOpen,
        bidLeadId,
        closeBidModal,
        openBidModal,
        handlePlaceBid,
        placeBidPending: placeBidMutation.isPending,
        handleApproveBid,
        approveBidPending: approveBidMutation.isPending,
        handleDeclineBid,
        declineBidPending: declineBidMutation.isPending,
        handleStartChat,
        startChatPending: startChatMutation.isPending,
        handleStateChange,
        patchStatePending: patchLeadStateMutation.isPending,
        patchStateLeadId: patchLeadStateMutation.variables?.leadId,
        canCreateLead,
        canPlaceBid,
        CATEGORY_OPTIONS,
        formatCurrencyInput,
        parseCurrencyInput,
    };
};
